import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Check, Crown, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import LangToggle from "../components/LangToggle";
import { billingAPI } from "../lib/api";
import { useLang } from "../lib/i18n";

const SUMUP_WIDGET_SDK_URL = "https://gateway.sumup.com/gateway/ecom/card/v2/sdk.js";
let sumUpSdkPromise;

function loadSumUpWidgetSdk() {
  if (window.SumUpCard) return Promise.resolve(window.SumUpCard);
  if (sumUpSdkPromise) return sumUpSdkPromise;
  sumUpSdkPromise = new Promise(function (resolve, reject) {
    const prior = document.getElementById("sumup-payment-widget-sdk");
    if (prior) prior.remove();
    const script = document.createElement("script");
    script.id = "sumup-payment-widget-sdk";
    script.src = SUMUP_WIDGET_SDK_URL;
    script.async = true;
    script.onload = function () { window.SumUpCard ? resolve(window.SumUpCard) : reject(new Error("SDK SumUp non disponibile")); };
    script.onerror = function () { reject(new Error("Caricamento widget SumUp non riuscito")); };
    document.head.appendChild(script);
  }).catch(function (error) { sumUpSdkPromise = undefined; throw error; });
  return sumUpSdkPromise;
}

const plans = [
  { code: "premium_monthly", title: "Premium mensile", price: "2,99 €", duration: "1 mese" },
  { code: "premium_annual", title: "Premium annuale", price: "19,99 €", duration: "1 anno" },
];

export default function UpgradePage({ user, onBack, onPremiumActivated }) {
  const { t } = useLang();
  const [selectedPlan, setSelectedPlan] = useState("premium_monthly");
  const [checkoutId, setCheckoutId] = useState("");
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(false);
  const [widgetLoading, setWidgetLoading] = useState(false);
  const [widgetError, setWidgetError] = useState("");
  const widgetRef = useRef(null);
  const isPremium = user.plan === "premium";

  const refreshBilling = async function () {
    try { setBilling((await billingAPI.subscription()).data); } catch (_) {}
  };

  useEffect(function () { if (isPremium) refreshBilling(); }, [isPremium]);

  const verifyCheckout = useCallback(async function () {
    try {
      const response = await billingAPI.verifyOneTimeCheckout(checkoutId);
      if (response.data?.status === "paid") {
        onPremiumActivated();
        toast.success(t.sumupPaid);
      } else {
        toast.info(t.billingPending);
      }
    } catch (error) {
      setWidgetError(error.response?.data?.detail || t.sumupPaymentFailed);
    }
  }, [checkoutId, onPremiumActivated, t.billingPending, t.sumupPaid, t.sumupPaymentFailed]);

  useEffect(function () {
    if (!checkoutId) return undefined;
    let cancelled = false;
    setWidgetLoading(true);
    setWidgetError("");
    (async function () {
      try {
        const SumUpCard = await loadSumUpWidgetSdk();
        if (cancelled || !document.getElementById("sumup-payment-widget")) return;
        widgetRef.current?.unmount?.();
        widgetRef.current = SumUpCard.mount({
          id: "sumup-payment-widget", checkoutId, country: "IT", locale: "it-IT", email: user.email,
          onResponse: async function (type) {
            if (type === "success") await verifyCheckout();
            if (type === "sent" || type === "auth-screen") setWidgetError("");
            if (type === "invalid") setWidgetError(t.sumupFormInvalid);
            if (type === "fail" || type === "error") setWidgetError(t.sumupPaymentFailed);
          },
        });
        if (!cancelled) setWidgetLoading(false);
      } catch (error) {
        console.error("Widget SumUp non caricato", error);
        if (!cancelled) { setWidgetLoading(false); setWidgetError(t.sumupWidgetError); }
      }
    })();
    return function () { cancelled = true; widgetRef.current?.unmount?.(); widgetRef.current = null; };
  }, [checkoutId, t.sumupFormInvalid, t.sumupPaymentFailed, t.sumupWidgetError, user.email, verifyCheckout]);

  const startCheckout = async function () {
    setLoading(true);
    setWidgetError("");
    try {
      const response = await billingAPI.createOneTimeCheckout({ plan_code: selectedPlan });
      const nextCheckoutId = response.data?.checkout_id;
      if (!nextCheckoutId || typeof nextCheckoutId !== "string") throw new Error("Checkout SumUp non valido");
      setCheckoutId(nextCheckoutId);
    } catch (error) {
      toast.error(error.response?.data?.detail || t.paymentSoon);
    } finally { setLoading(false); }
  };

  return <main className="min-h-screen bg-[#F8FAFC]" data-testid="upgrade-page">
    <header className="flex items-center gap-4 border-b border-slate-200 bg-white px-6 py-3">
      <Button data-testid="upgrade-back-button" variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="mr-2 w-4" />{t.goBack}</Button>
      <h1 className="text-lg font-bold text-slate-800">{t.upgradeTitle}</h1><div className="flex-1" /><LangToggle />
    </header>
    <div className="mx-auto max-w-4xl p-6 sm:p-8">
      <p className="mb-8 text-center text-slate-500">{t.sumupOneTimeNotice}</p>
      {isPremium ? <section className="mx-auto max-w-xl border border-amber-200 bg-white p-8 text-center" data-testid="sumup-current-plan">
        <Crown className="mx-auto mb-3 w-7 text-amber-500" /><h2 className="text-xl font-bold text-slate-800">{t.currentPlan}</h2>
        {billing?.current_period_end && <p className="mt-2 text-sm text-slate-500" data-testid="sumup-period-end">Accesso fino al {new Date(billing.current_period_end).toLocaleDateString("it-IT")}</p>}
        <Button data-testid="sumup-refresh-status-button" variant="outline" className="mt-5" onClick={refreshBilling}>{t.billingRefresh}</Button>
      </section> : <div className="grid gap-6 md:grid-cols-3">
        <section className="border border-slate-200 bg-white p-6" data-testid="free-plan-card"><Lock className="mb-4 w-5 text-slate-400" /><h2 className="text-xl font-bold">{t.freePlan}</h2><p className="my-5 text-2xl font-bold">€0</p><p className="text-sm text-slate-600">{t.free10}</p></section>
        <section className="border border-emerald-300 bg-white p-6 md:col-span-2" data-testid="sumup-one-time-plans">
          <h2 className="flex items-center gap-2 text-xl font-bold"><Crown className="w-5 text-amber-500" />{t.premiumPlan}</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">{plans.map(function (plan) {
            const selected = plan.code === selectedPlan;
            return <button type="button" key={plan.code} data-testid={`sumup-plan-${plan.code}`} disabled={!!checkoutId} onClick={function () { setSelectedPlan(plan.code); }} className={`border p-4 text-left transition-colors ${selected ? "border-emerald-700 bg-emerald-50" : "border-slate-200 hover:border-emerald-300"}`}>
              <span className="block font-semibold text-slate-800">{plan.title}</span><span className="mt-1 block text-2xl font-bold text-slate-900">{plan.price}</span><span className="text-sm text-slate-500">{plan.duration} · {t.sumupOneTime}</span>
            </button>;
          })}</div>
          {!checkoutId && <Button data-testid="sumup-start-one-time-checkout-button" disabled={loading} className="mt-5 w-full bg-emerald-700 text-white hover:bg-emerald-800" onClick={startCheckout}><Crown className="mr-2 w-4" />{t.sumupSetup}</Button>}
          {checkoutId && <div className="mt-5 space-y-3" data-testid="sumup-widget-stage"><p className="text-sm text-slate-600" data-testid="sumup-widget-status">{widgetError || (widgetLoading ? t.sumupLoading : t.sumupCard)}</p><div id="sumup-payment-widget" className="min-h-[280px] w-full" data-testid="sumup-payment-widget" />{widgetError && <Button data-testid="sumup-widget-retry-button" variant="outline" className="w-full" onClick={function () { setCheckoutId(""); setWidgetError(""); }}>{t.sumupSetup}</Button>}</div>}
          <div className="mt-5 flex gap-2 text-sm text-slate-600"><Check className="mt-0.5 w-4 shrink-0 text-emerald-600" />{t.sumupServerVerification}</div>
        </section>
      </div>}
    </div>
  </main>;
}